import MongoLeafIcon from "@/components/icons/MongoLeafIcon";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  calculateCapacityPercentage,
  formatBytes,
  formatDateTime,
  formatBackupFrequency
} from "@/lib/utils";
import { DatabaseDetails, DatabaseStatus } from "@/types/database";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Code,
  Copy,
  Database,
  Eye,
  HardDrive,
  Server,
  XCircle,
  Archive,
  ExternalLink,
  Network
} from "lucide-react";
import React, { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import CapacityWarning from "./CapacityWarning";
import ConnectionManagementDialog from "./ConnectionManagementDialog";
import { databaseApi } from "@/lib/api";

interface DatabaseListProps {
  databases: DatabaseDetails[];
  onViewDetails: (database: DatabaseDetails) => void;
  isLoading?: boolean;
}

const DatabaseList: React.FC<DatabaseListProps> = ({
  databases,
  onViewDetails,
  isLoading = false,
}) => {
  const { toast, ToastContainer } = useToast();
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [selectedDatabase, setSelectedDatabase] = useState<DatabaseDetails | null>(null);

  const getStatusIcon = (status: DatabaseStatus) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "suspended":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "deleted":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: DatabaseStatus) => {
    switch (status) {
      case "active":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "suspended":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "deleted":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getDatabaseIcon = (dbtype: string) => {
    switch (dbtype) {
      case "MYSQL":
        return "🐬";
      case "POSTGRES":
        return "🐘";
      case "MONGODB":
        return <MongoLeafIcon height={28} />;
      default:
        return "🗄️";
    }
  };

  const getDatabaseClassIcon = (databaseClass: string) => {
    switch (databaseClass) {
      case "DEV":
        return <Code className="h-4 w-4 text-blue-500" />;
      case "PROD":
        return <Server className="h-4 w-4 text-red-500" />;
      default:
        return <Code className="h-4 w-4 text-gray-500" />;
    }
  };

  const getDatabaseClassColor = (databaseClass: string) => {
    switch (databaseClass) {
      case "DEV":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "PROD":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };



  /**
   * Copies the full database name to clipboard
   * @param dbname - The full database name to copy
   */
  const copyToClipboard = async (dbname: string) => {
    try {
      await navigator.clipboard.writeText(dbname);
      toast({
        title: "Copied!",
        description: "Full database name copied to clipboard",
        status: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        status: "error",
      });
    }
  };

  /**
   * Opens the connection management dialog for a database
   * @param database - The database to manage connections for
   */
  const handleOpenConnectionDialog = (database: DatabaseDetails) => {
    setSelectedDatabase(database);
    setConnectionDialogOpen(true);
  };

  /**
   * Closes the connection management dialog
   */
  const handleCloseConnectionDialog = () => {
    setConnectionDialogOpen(false);
    setSelectedDatabase(null);
  };

  /**
   * Opens quickdb-web-client with the database connection string
   * @param database - The database to connect to
   */
  const handleConnectToWebClient = async (database: DatabaseDetails) => {
    try {
      // Fetch connection strings
      const response = await databaseApi.getConnectionStrings(database.dbname);
      
      if (!response.success || !response.data) {
        throw new Error(response.error || "Failed to fetch connection string");
      }

      // Use read-only connection string for safety
      const connectionString = response.data.connectionStrings?.ro;
      
      if (!connectionString) {
        throw new Error("Read-only connection string not available");
      }

      // Get web client URL from environment or use default
      const webClientUrl = import.meta.env.VITE_WEB_CLIENT_URL || "http://localhost:3010";
      
      // Encode connection string for URL parameter
      const encodedConnectionString = encodeURIComponent(connectionString);
      
      // Open web client in new window with connection string as parameter
      const webClientWindow = window.open(
        `${webClientUrl}?connectionString=${encodedConnectionString}`,
        "_blank"
      );

      if (!webClientWindow) {
        throw new Error("Popup blocked. Please allow popups for this site.");
      }

      toast({
        title: "Opening Database Client",
        description: "Database client is opening in a new window",
        status: "success",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to connect to database client",
        status: "error",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading databases...</span>
      </div>
    );
  }

  if (databases.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Database className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No databases found</h3>
          <p className="text-muted-foreground text-center">
            Create your first database to get started.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <ToastContainer />
      <ConnectionManagementDialog
        isOpen={connectionDialogOpen}
        onClose={handleCloseConnectionDialog}
        database={selectedDatabase}
      />
      <div className="space-y-4">
        {databases.map((database) => (
        <div key={database.id}>
          <CapacityWarning database={database} />
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {getDatabaseIcon(database.dbtype)}
                  </span>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-lg">
                        {database.dbname.replace(/-[^-]+$/, "")}
                      </CardTitle>
                      {/* Tags Display */}
                      {database.tags && database.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {database.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Full Database Name Display */}
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs text-gray-500">
                        Full database name:{" "}
                        <span className="font-mono">{database.dbname}</span>
                      </span>
                      <button
                        onClick={() => copyToClipboard(database.dbname)}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                        title="Copy full database name"
                        type="button"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                    <CardDescription className="flex items-center gap-2">
                      <span className="capitalize">{database.dbtype}</span>
                      <span>•</span>
                      <span>{database.size}</span>
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium border ${getDatabaseClassColor(
                      database.databaseClass
                    )}`}
                  >
                    <div className="flex items-center gap-1">
                      {getDatabaseClassIcon(database.databaseClass)}
                      <span>{database.databaseClass}</span>
                    </div>
                  </span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                      database.status
                    )}`}
                  >
                    <div className="flex items-center gap-1">
                      {getStatusIcon(database.status)}
                      <span className="capitalize">
                        {database.status.replace("_", " ")}
                      </span>
                    </div>
                  </span>
                  {database.backupEnabled && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200">
                      <div className="flex items-center gap-1">
                        <Archive className="h-4 w-4" />
                        <span>Backup Enabled</span>
                      </div>
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Simple Capacity Display */}
              {database.current_usage_bytes > 0 && (
                <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <HardDrive className="h-4 w-4" />
                  <span>
                    {formatBytes(database.current_usage_bytes)} /{" "}
                    {formatBytes(database.capacity_limit_bytes)}(
                    {calculateCapacityPercentage(
                      database.current_usage_bytes,
                      database.capacity_limit_bytes
                    )}
                    % used)
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Created: {formatDateTime(database.created_at)}</div>
                  {database.backupEnabled && (
                    <div className="space-y-1">
                      {database.lastBackupAt ? (
                        <div>
                          Last backup: {formatDateTime(database.lastBackupAt)}
                          {database.lastBackupStatus && 
                           database.lastBackupStatus !== "SKIPPED" && (
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                              database.lastBackupStatus === "COMPLETED"
                                ? "bg-green-100 text-green-700"
                                : database.lastBackupStatus === "FAILED"
                                ? "bg-red-100 text-red-700"
                                : database.lastBackupStatus === "IN_PROGRESS"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-700"
                            }`}>
                              {database.lastBackupStatus}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="text-gray-400 italic">No backups taken yet</div>
                      )}
                      {database.backupFrequency && (
                        <div>Frequency: Every {formatBackupFrequency(database.backupFrequency)}</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenConnectionDialog(database)}
                    disabled={database.status !== "active"}
                    title={database.status !== "active" ? "Database must be active to manage connections" : "Manage Connections"}
                  >
                    <Network className="h-4 w-4 mr-1" />
                    Manage Connections
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleConnectToWebClient(database)}
                    disabled={database.status !== "active"}
                    title={database.status !== "active" ? "Database must be active to connect" : "Open in Database Client"}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Connect
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(database)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Details
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ))}
      </div>
    </>
  );
};

export default DatabaseList;
